import { Box } from "@mui/material";
import type { SxProps, SystemStyleObject } from "@mui/system";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Hints } from "../../../components/ui";
import { QuestionContentFrame } from "../../../components/ui/QuestionContent";
import { useThemeContext } from "../../../globalContext/global-context";
import { hostThemes } from "../../../theme/hostThemes";
import type { ThemeV2 } from "../../../util/types";
import type {
  QuestionArrangement,
  QuestionPrompt,
  QuestionSpace,
} from "../../contracts";
import {
  AnswerContent,
  QuestionAnnouncement,
  type QuestionHostFrameProps,
} from "../../QuestionFrameShared";
import {
  FormsHostControls,
  FormsPendingOverlay,
  formsInputSurface,
} from "./util/FormsControls";

const formsLayout = (theme: ThemeV2, split: boolean): SystemStyleObject => ({
  display: "flex",
  justifyContent: split ? "space-between" : "safe center",
  alignItems: "center",
  flexDirection: split ? "row" : "column",
  height: "100%",
  "& > *": {
    flexBasis: split ? `calc(50% - ${theme.tokens.spacing["5"]}px)` : undefined,
    width: split ? undefined : "100%",
  },
});

const compactPrompt: SxProps = {
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const compactResponse: SxProps = {
  width: "100%",
  display: "flex",
  justifyContent: "safe center",
  alignItems: "flex-end",
  flexDirection: "column",
};

const FormsResponseFrame = ({
  response,
  input,
  showChoiceHint,
  choiceHint,
  prompt,
}: Pick<
  QuestionHostFrameProps,
  "response" | "input" | "showChoiceHint" | "choiceHint" | "prompt"
>) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <Box sx={{ width: "100%" }}>
      {showChoiceHint && (
        <Box sx={{ width: "100%" }}>
          <Hints
            text={theme.copy.hints[choiceHint ?? "multichoice"]}
            variant="hint"
          />
          {prompt.error && <Hints text={prompt.error} variant="error" />}
        </Box>
      )}
      {input === "custom" ? (
        <Box
          data-testid="forms-custom-input-surface"
          sx={{
            ...formsInputSurface(theme),
            minHeight: 96,
            display: "flex",
            alignItems: "center",
            paddingInline: `${theme.tokens.spacing["4"]}px`,
          }}
        >
          <Box sx={{ width: "100%" }}>{response}</Box>
        </Box>
      ) : (
        response
      )}
    </Box>
  );
};

const FormsQuestionHost = ({
  arrangement,
  space,
  answerContent,
  prompt,
  busy,
  primaryActionPending,
}: {
  arrangement: QuestionArrangement;
  space: QuestionSpace;
  answerContent: React.ReactNode;
  prompt: QuestionPrompt;
  busy: boolean;
  primaryActionPending: boolean;
}) => {
  const isWide = arrangement === "wide";
  const split = isWide && space === "standard";
  const fillAvailableImageSpace =
    isWide && space === "expanded" && Boolean(prompt.image);
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const hasPrompt = Boolean(prompt.content || prompt.image || prompt.error);
  const rootRef = useRef<HTMLDivElement>(null);
  const lastControlRef = useRef<{ element: HTMLElement; at: number } | null>(
    null,
  );
  const [pendingAnchor, setPendingAnchor] = useState<HTMLElement | null>(null);
  const showPending = busy || primaryActionPending;

  useLayoutEffect(() => {
    if (primaryActionPending) {
      setPendingAnchor(
        rootRef.current!.querySelector<HTMLElement>(
          '[data-testid="landscape-send-button"]',
        ),
      );
      return;
    }
    if (!busy) {
      setPendingAnchor(null);
      return;
    }
    const recent = lastControlRef.current;
    const focused = document.activeElement;
    setPendingAnchor(
      recent && Date.now() - recent.at < 1000 && recent.element.isConnected
        ? recent.element
        : focused instanceof HTMLElement && rootRef.current?.contains(focused)
          ? focused
          : null,
    );
  }, [busy, primaryActionPending]);

  return (
    <Box
      ref={rootRef}
      data-testid="chat-question"
      onClickCapture={(event) => {
        const target = event.target;
        const button =
          target instanceof Element ? target.closest("button") : null;
        if (button && event.currentTarget.contains(button))
          lastControlRef.current = { element: button, at: Date.now() };
      }}
      sx={{ height: "100%", position: "relative" }}
    >
      {hasPrompt && <QuestionAnnouncement prompt={prompt} />}
      <Box
        data-testid={`question-host-forms-${arrangement}`}
        aria-busy={showPending}
        sx={[
          formsLayout(theme, split),
          {
            gap: `${theme.tokens.spacing["5"]}px`,
            color: theme.tokens.color.text.primary,
            minHeight: 0,
            alignItems: split ? "safe center" : undefined,
          },
          fillAvailableImageSpace
            ? {
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                gridTemplateRows: `minmax(${hostThemes.forms.questionImage.expandedMinHeight}px, 1fr) auto`,
                alignItems: "stretch",
                "& > *": {
                  width: "100%",
                  minWidth: 0,
                  minHeight: 0,
                },
              }
            : {},
        ]}
      >
        {hasPrompt && (
          <Box
            sx={
              split
                ? {
                    "& > [data-testid='question-content']": {
                      minHeight: hostThemes.forms.questionPanel.wideMinHeight,
                      height: "auto",
                    },
                  }
                : {
                    ...compactPrompt,
                    ...(isWide ? { alignItems: "stretch" } : {}),
                    ...(fillAvailableImageSpace ? { minHeight: 0 } : {}),
                    "& > [data-testid='question-content']": {
                      height: fillAvailableImageSpace ? "100%" : "auto",
                      width: isWide ? "100%" : undefined,
                    },
                  }
            }
          >
            <QuestionContentFrame
              content={prompt.content}
              error={prompt.error}
              image={prompt.image}
              altText={prompt.altText}
              orientation={isWide ? "landscape" : "portrait-land"}
              imageMaxHeight={
                fillAvailableImageSpace
                  ? "100%"
                  : hostThemes.forms.questionImage.maxHeight
              }
              fillAvailableImageSpace={fillAvailableImageSpace}
            />
          </Box>
        )}
        <Box sx={split ? {} : compactResponse}>{answerContent}</Box>
      </Box>
      <FormsPendingOverlay open={showPending} anchorEl={pendingAnchor} />
    </Box>
  );
};

export const FormsQuestionFrame = (props: QuestionHostFrameProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== "Enter" ||
        event.repeat ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.isComposing ||
        (event.target instanceof HTMLElement &&
          event.target.closest(
            "input, textarea, select, button, a, [role='button'], [role='link'], [role='slider'], [role='radio'], [role='checkbox'], [role='dialog'], [contenteditable='true']",
          ))
      )
        return;
      if (props.runSuggestedAction?.()) event.preventDefault();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [props.runSuggestedAction]);

  return (
    <FormsQuestionHost
      arrangement={props.arrangement}
      space={props.space}
      prompt={props.prompt}
      busy={props.busy}
      primaryActionPending={Boolean(props.controls.primaryAction?.pending)}
      answerContent={
        <>
          {props.inputHint && (
            <Hints text={theme.copy.hints[props.inputHint]} variant="hint" />
          )}
          <AnswerContent
            primaryActionTestId="landscape-send-button"
            renderControls={(controls) => (
              <FormsHostControls
                controls={controls}
                input={props.input}
                arrangement={props.arrangement}
              />
            )}
            controls={props.controls}
            primaryActionOwner={props.primaryActionOwner}
            input={props.input}
            responseContent={<FormsResponseFrame {...props} />}
          />
        </>
      }
    />
  );
};
